import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, TextField, Chip } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";

const OTP_TTL = 10 * 60; // 10 minutes, tracked client-side (no server countdown)

// Verification flags are NOT returned by GET /profile — they live in app state
// from login/registration. We read + write localStorage so the whole app stays
// consistent (Dashboard guard, 403 interceptor, etc.).
const PhoneVerificationCard = ({ phone }) => {
  const { showAlert } = useAlert();
  const [verified, setVerified] = useState(
    localStorage.getItem("isPhoneVerified") === "true"
  );
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [remaining, setRemaining] = useState(0); // seconds until the code expires
  const timerRef = useRef(null);

  useEffect(() => {
    return () => timerRef.current && clearInterval(timerRef.current);
  }, []);

  const startExpiryCountdown = () => {
    setRemaining(OTP_TTL);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const result = await AuthService.sendPhoneOtp();
      if (result?.success !== false) {
        setOtpSent(true);
        setOtp("");
        startExpiryCountdown();
        showAlert("A verification code has been sent to your phone.", "success");
      } else {
        showAlert(result?.message || "Could not send the code. Please try again.", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Could not send the code. Please try again.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!otp) return;
    setVerifying(true);
    try {
      const result = await AuthService.verifyPhoneOtp(otp.trim());
      if (result?.success && result?.data?.isPhoneVerified) {
        localStorage.setItem("isPhoneVerified", "true");
        setVerified(true);
        setOtpSent(false);
        if (timerRef.current) clearInterval(timerRef.current);
        showAlert("Your phone number has been verified.", "success");
      } else {
        showAlert(result?.message || "Invalid or expired code. Please try again.", "error");
      }
    } catch (error) {
      showAlert(error?.message || "Something went wrong. Please try again.", "error");
    } finally {
      setVerifying(false);
    }
  };

  const mmss = (secs) =>
    `${String(Math.floor(secs / 60)).padStart(1, "0")}:${String(secs % 60).padStart(2, "0")}`;
  const expired = otpSent && remaining === 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        p: 2,
        mb: 2,
        borderRadius: "12px",
        border: "1px solid #ececec",
        backgroundColor: "#fafafa",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#202020" }}>
          Phone verification
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#878787" }}>
          {phone ? phone : "No phone number on file"}
        </Typography>
      </Box>

      {verified ? (
        <Chip
          icon={<CheckCircleIcon />}
          label="Verified"
          color="success"
          variant="outlined"
          size="small"
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {otpSent && (
            <TextField
              size="small"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              placeholder="6-digit code"
              disabled={expired}
              sx={{ width: 140 }}
            />
          )}

          {otpSent && !expired && (
            <Typography sx={{ fontSize: "11px", color: "#878787", minWidth: 78 }}>
              Expires in {mmss(remaining)}
            </Typography>
          )}
          {expired && (
            <Typography sx={{ fontSize: "11px", color: "#d32f2f", minWidth: 78 }}>
              Code expired
            </Typography>
          )}

          {otpSent && !expired && (
            <LoadingButton
              type="button"
              loading={verifying}
              onClick={handleVerify}
              variant="contained"
              size="small"
              disabled={otp.length < 4}
              sx={{ textTransform: "none", backgroundColor: "#000" }}
            >
              Verify
            </LoadingButton>
          )}

          <LoadingButton
            type="button"
            loading={sending}
            onClick={handleSend}
            variant={otpSent ? "text" : "contained"}
            size="small"
            disabled={!phone}
            sx={{
              textTransform: "none",
              ...(otpSent ? {} : { backgroundColor: "#000" }),
            }}
          >
            {otpSent ? "Resend code" : "Verify phone"}
          </LoadingButton>
        </Box>
      )}
    </Box>
  );
};

export default PhoneVerificationCard;
